import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BillDetailTile } from '@/components/BillDetailTile';
import { usePickupStore } from '@/stores/pickupStore';
import { gpsService } from '@/services/gps';

export function PickupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loadStop = usePickupStore((s) => s.loadStop);
  const startEnRoute = usePickupStore((s) => s.startEnRoute);
  const stop = usePickupStore((s) => s.stop);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) void loadStop(id);
    // Warm the GPS watch now so a fix is cached before EN ROUTE is tapped.
    gpsService.primeFromGesture();
  }, [id, loadStop]);

  if (!stop) {
    return (
      <AppShell title="Pickup" showBack>
        <p className="text-brand-muted">Loading...</p>
      </AppShell>
    );
  }

  const first = stop.bills[0];
  const address = [stop.address, first?.remShipperSuite].filter(Boolean).join(' ');
  const callerName = first?.remCallerName?.trim();
  const phone = first?.remShipperPhone?.trim();
  const note = first?.remShipperNote?.trim();

  const accept = async () => {
    setBusy(true);
    try {
      gpsService.primeFromGesture();
      await startEnRoute();
      navigate(`/load/${id}/progress`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={stop.name || 'Shipper'} showBack>
      <Card accent="pickup">
        <CardBody>
          <div className="text-lg font-bold">{stop.name || '--'}</div>
          <div className="text-sm text-brand-muted">
            {address || '--'}
            {stop.city ? `, ${stop.city}` : ''}
          </div>
          {(callerName || phone) && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 text-brand-pickup" />
              {callerName && <span className="text-brand-muted">{callerName}</span>}
              {phone && (
                <a href={`tel:${phone}`} className="font-semibold text-brand-pickup">
                  {phone}
                </a>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {note && (
        <div className="mt-3 rounded-md border border-brand-border p-3">
          <div className="text-xs font-bold uppercase text-brand-muted">Shipper notes</div>
          <div className="mt-1 text-sm">{note}</div>
        </div>
      )}

      <ul className="mt-3 space-y-2">
        {stop.bills.map((b) => (
          <li key={b.remF04Id}>
            <BillDetailTile assignment={b} kind="pickup" defaultOpen={stop.bills.length === 1} />
          </li>
        ))}
      </ul>

      <div className="mt-4">
        <Button variant="pickup" size="xl" fullWidth disabled={busy} onClick={accept}>
          {busy ? 'Starting...' : 'EN ROUTE'}
        </Button>
      </div>
    </AppShell>
  );
}
